<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="860" scrollable>
    <v-card>
      <v-card-title class="primary white--text py-3">
        <v-icon left color="white">link</v-icon>
        Anagrafiche collegate
        <v-spacer />
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4" style="max-height: 70vh">
        <p class="text-body-2 text--secondary">
          Quando un cliente cambia ragione sociale e si fattura con una nuova anagrafica, collega quella vecchia
          (che inizia con "*") alla nuova. Nelle statistiche di ore e fatture le due anagrafiche diventano un cliente
          solo, con lo storico unito: niente più clienti a rischio per un semplice cambio di nome.
          I dati originali non vengono modificati e il collegamento si può rimuovere in qualsiasi momento.
        </p>

        <v-progress-linear v-if="caricamento" indeterminate class="mb-3" />

        <v-alert v-if="stato && !stato.disponibile" type="warning" dense text>
          La tabella dei collegamenti non esiste ancora: eseguire sul database lo script
          <code>api/Database/WEB_AnagraficheCollegate.sql</code>.
        </v-alert>

        <template v-if="stato && stato.disponibile">
          <!-- Anagrafiche con "*" ancora da collegare -->
          <div class="text-subtitle-2 mb-2">Da collegare ({{ stato.daCollegare.length }})</div>

          <div v-if="!stato.daCollegare.length" class="text-body-2 text--secondary mb-4">
            Nessuna anagrafica con ragione sociale che inizia con "*" da collegare.
          </div>

          <v-row v-for="r in stato.daCollegare" :key="r.codAnagrafica" dense align="center" class="mb-1">
            <v-col cols="12" md="4">
              <div class="font-weight-medium">{{ r.ragSoc }}</div>
              <div class="text-caption text--secondary">
                {{ r.codAnagrafica }}<span v-if="r.piva"> · P.IVA {{ r.piva }}</span>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <v-autocomplete v-model="scelte[r.codAnagrafica]" :items="anagraficheNuove" item-text="ragSoc"
                item-value="codAnagrafica" label="Nuova anagrafica" dense clearable
                no-data-text="Nessun dato disponibile" :hint="suggerimento(r)" persistent-hint />
            </v-col>

            <v-col cols="12" md="2" class="text-right">
              <v-btn small color="primary" :disabled="!scelte[r.codAnagrafica] || occupato"
                @click="collega(r)">
                Collega
              </v-btn>
            </v-col>
          </v-row>

          <!-- Collegamenti esistenti -->
          <div class="text-subtitle-2 mt-5 mb-2">Collegate ({{ stato.collegamenti.length }})</div>

          <div v-if="!stato.collegamenti.length" class="text-body-2 text--secondary">
            Nessun collegamento attivo.
          </div>

          <v-simple-table v-else dense>
            <thead>
              <tr>
                <th>Anagrafica vecchia</th>
                <th>Conteggiata come</th>
                <th class="text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in stato.collegamenti" :key="c.codVecchio">
                <td>{{ c.ragSocVecchia || c.codVecchio }}</td>
                <td>{{ c.ragSocNuova || c.codNuovo }}</td>
                <td class="text-right">
                  <v-btn small text color="error" :disabled="occupato" @click="scollega(c)">Scollega</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Chiudi</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { get_collegamenti_anagrafiche, collega_anagrafiche, scollega_anagrafica } from "@/api/api.clienti.js";

export default {
  name: "DialogCollegamentiAnagrafiche",

  props: {
    value: { type: Boolean, default: false },
    elencoClienti: { type: Array, default: () => [] }
  },

  data: () => ({
    stato: null,
    caricamento: false,
    occupato: false,

    // Anagrafica nuova scelta per ciascuna vecchia (chiave: codice della vecchia)
    scelte: {}
  }),

  computed: {
    // Le anagrafiche tra cui scegliere la nuova: quelle senza "*" davanti
    anagraficheNuove() {
      return this.elencoClienti.filter(c => c.ragSoc && !c.ragSoc.startsWith("*"));
    }
  },

  methods: {
    async carica() {
      this.caricamento = true;
      try {
        const res = await get_collegamenti_anagrafiche();
        this.stato = res.data;
        this.proponi();
      } catch (error) {
        console.error("Errore caricamento collegamenti:", error);
      } finally {
        this.caricamento = false;
      }
    },

    // Preseleziona il primo suggerimento (stessa partita IVA, stesso codice fiscale o stesso nome)
    proponi() {
      const scelte = {};
      for (const r of this.stato.daCollegare) {
        const precedente = this.scelte[r.codAnagrafica];
        scelte[r.codAnagrafica] = precedente || (r.suggerimenti.length === 1 ? r.suggerimenti[0].codAnagrafica : null);
      }
      this.scelte = scelte;
    },

    suggerimento(r) {
      if (!r.suggerimenti.length) return "Nessun suggerimento: scegli la nuova anagrafica";
      return "Suggerita: " + r.suggerimenti.map(s => `${s.ragSoc} (${s.motivo})`).join(", ");
    },

    async collega(r) {
      this.occupato = true;
      try {
        await collega_anagrafiche({ codVecchio: r.codAnagrafica, codNuovo: this.scelte[r.codAnagrafica] });
        this.$toast?.success("Anagrafiche collegate");
        await this.carica();
        this.$emit("cambiato", this.stato);
      } catch (error) {
        this.$toast?.error(this.messaggioErrore(error, "Errore durante il collegamento"));
      } finally {
        this.occupato = false;
      }
    },

    async scollega(c) {
      const conferma = await this.$confirm?.open(
        "Scollega",
        `Scollegare "${c.ragSocVecchia || c.codVecchio}"? Le sue ore e fatture torneranno a essere contate separatamente.`
      );
      if (!conferma) return;

      this.occupato = true;
      try {
        await scollega_anagrafica(c.codVecchio);
        this.$toast?.success("Collegamento rimosso");
        await this.carica();
        this.$emit("cambiato", this.stato);
      } catch (error) {
        this.$toast?.error(this.messaggioErrore(error, "Errore durante lo scollegamento"));
      } finally {
        this.occupato = false;
      }
    },

    // Messaggio restituito dall'API (es. validazione), altrimenti quello di default
    messaggioErrore(error, predefinito) {
      const dati = error && error.response && error.response.data;
      return typeof dati === "string" && dati ? dati : predefinito;
    }
  },

  watch: {
    value(aperto) {
      if (aperto) this.carica();
    }
  }
}
</script>
